import React, { useEffect, useMemo, useState } from "react";
import { MoreHorizontal, ChevronRight } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { supabase } from "../supabaseClient";
import AdminTopBar from "./AdminTopBar";
import SearchableSelect from "./SearchableSelect";
import {
  CARD_RANGE_OPTIONS,
  STAT_PANEL_KEYS,
  buildTierIndex,
  computeDashboardStats,
  filterTicketsForEvent,
  getRangeLabel,
  getSharedRange,
  isRefundedTicket,
} from "./dashboardStats";
import {
  adminEmpty,
  adminRow,
  adminStatusPill,
  adminTableHead,
  adminTd,
  adminTdName,
} from "./adminUi";
import { deriveAdminUiStatus } from "../lib/proposalContract";
import { isMissingSupportTicketsSchemaError } from "../lib/supportTickets";

import { getComputedEventStatus, parseEventDateTime } from "../lib/eventPublicStatus";
import AdminEventCalendar from "./AdminEventCalendar";

const TICKET_BASE_COLUMNS = ["id", "created_at", "ticket_tier_id"];
const TICKET_OPTIONAL_COLUMNS = ["refunded_at", "is_validated", "validated_at"];

const fetchDashboardTickets = async () => {
  let optionalCols = [...TICKET_OPTIONAL_COLUMNS];
  const run = () => supabase.from("tickets").select([...TICKET_BASE_COLUMNS, ...optionalCols].join(", "));

  let result = await run();
  while (result.error && String(result.error.code || "") === "42703" && optionalCols.length) {
    const message = String(result.error.message || "").toLowerCase();
    const missing = optionalCols.find((col) => message.includes(col)) || optionalCols[0];
    optionalCols = optionalCols.filter((col) => col !== missing);
    result = await run();
  }
  return result;
};

const SALES_RANGE_OPTIONS = [
  { value: "day", label: "Day" },
  { value: "week", label: "Week" },
  { value: "month", label: "Month" },
  { value: "year", label: "Year" },
];

const MANILA_TIME_ZONE = "Asia/Manila";
const DAY_MS = 24 * 60 * 60 * 1000;
const MONTH_LABELS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

const manilaDatePartFormatter = new Intl.DateTimeFormat("en-US", {
  timeZone: MANILA_TIME_ZONE,
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
});

const getComputedStatus = (event) => getComputedEventStatus(event);

const formatCurrency = (amount) => {
  return `PHP ${Number(amount || 0).toLocaleString()}`;
};

const formatShortDate = (date) => {
  if (!date) return "-";
  const parsed = new Date(`${date}T00:00:00`);
  if (Number.isNaN(parsed.getTime())) return date;
  return parsed.toLocaleDateString("en-PH", {
    timeZone: MANILA_TIME_ZONE,
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const extractDateParts = (dateInput, formatter) => {
  const parsed = new Date(dateInput);
  if (Number.isNaN(parsed.getTime())) return null;

  const partMap = {};
  formatter.formatToParts(parsed).forEach((part) => {
    if (part.type !== "literal") {
      partMap[part.type] = part.value;
    }
  });

  return partMap;
};

const getManilaDateParts = (dateInput) => extractDateParts(dateInput, manilaDatePartFormatter);

const getManilaDaySerial = (dateInput) => {
  const parts = getManilaDateParts(dateInput);
  if (!parts) return null;
  return Math.floor(Date.UTC(Number(parts.year), Number(parts.month) - 1, Number(parts.day)) / DAY_MS);
};

const getManilaMonthIndex = (dateInput) => {
  const parts = getManilaDateParts(dateInput);
  if (!parts) return null;
  return Number(parts.year) * 12 + (Number(parts.month) - 1);
};

const toDayKey = (dateInput) => {
  const parts = getManilaDateParts(dateInput);
  if (!parts) return "";
  return `${parts.year}-${parts.month}-${parts.day}`;
};

const StatCard = ({
  title,
  value,
  subtitle,
  badge,
  badgeClass,
  isFilterOpen,
  onToggleFilter,
  onSelectFilter,
  selectedRange,
  onOpen,
}) => (
  <div className="bg-white border border-gray-300 rounded-2xl p-4 flex justify-between gap-2 min-h-[98px] min-w-0">
    <button
      type="button"
      onClick={onOpen}
      disabled={!onOpen}
      className={`min-w-0 text-left ${onOpen ? "cursor-pointer" : "cursor-default"}`}
    >
      <p className="text-lg leading-tight font-black text-gray-900">{title}</p>
      <p className="text-2xl font-black mt-1 text-gray-900">{value}</p>
      {subtitle && <p className="text-[11px] text-gray-500 mt-1">{subtitle}</p>}
    </button>
    <div className="relative flex flex-col items-end justify-between" data-filter-menu="true">
      <button onClick={onToggleFilter} className="p-0.5 rounded hover:bg-gray-100" data-filter-menu="true">
        <MoreHorizontal size={16} className="text-gray-500" />
      </button>

      {isFilterOpen && (
        <div className="absolute top-7 right-0 w-36 rounded-lg border border-gray-200 bg-white shadow-xl z-20" data-filter-menu="true">
          {CARD_RANGE_OPTIONS.map((option) => (
            <button
              key={option.value}
              onClick={() => onSelectFilter(option.value)}
              className={`w-full px-3 py-2 text-left text-xs font-semibold hover:bg-gray-50 ${
                selectedRange === option.value ? "text-brand-maroon" : "text-gray-700"
              }`}
              data-filter-menu="true"
            >
              {option.label}
            </button>
          ))}
        </div>
      )}

      {badge && (
        <span className={`text-xs px-3 py-1 rounded-full h-fit font-bold ${badgeClass}`}>
          {badge}
        </span>
      )}
    </div>
  </div>
);

const EventRow = ({ row, index, highlighted = false }) => {
  const navigate = useNavigate();
  const progress = row.total > 0 ? Math.min(100, Math.round((row.sold / row.total) * 100)) : 0;

  return (
    <tr
      className={`${adminRow} cursor-pointer ${highlighted ? "bg-amber-50 hover:bg-amber-100" : "hover:bg-gray-50"}`}
      onClick={() => navigate(`/admin/create-event?eventId=${row.id}`)}
    >
      <td className={adminTdName}>{index + 1}.) {row.title}</td>
      <td className={`${adminTd} text-center`}>{formatShortDate(row.date)}</td>
      <td className="px-4 py-4">
        <div className="h-2 bg-gray-200 rounded">
          <div className="h-2 bg-blue-500 rounded" style={{ width: `${progress}%` }} />
        </div>
      </td>
      <td className="px-4 py-4 text-center">
        <span className={`${adminStatusPill} ${row.statusClass}`}>
          {row.statusLabel}
        </span>
      </td>
    </tr>
  );
};

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [dataError, setDataError] = useState("");
  const [selectedEventId, setSelectedEventId] = useState("");
  const [panelRanges, setPanelRanges] = useState({
    revenue: "30d",
    ticketsSold: "30d",
    activeEvents: "30d",
    supportTickets: "30d",
  });
  const [openCardFilter, setOpenCardFilter] = useState("");
  const [rawDashboardData, setRawDashboardData] = useState({
    events: [],
    tiers: [],
    tickets: [],
    bookings: [],
    supportTickets: [],
  });
  const [selectedTicketEventId, setSelectedTicketEventId] = useState("");
  const [isTicketEventMenuOpen, setIsTicketEventMenuOpen] = useState(false);
  const [eventRows, setEventRows] = useState([]);
  const [scheduleEvents, setScheduleEvents] = useState([]);
  const [salesRange, setSalesRange] = useState("week");
  const [pendingBookings, setPendingBookings] = useState([]);

  useEffect(() => {
    const fetchDashboardData = async () => {
      const [
        { data: eventsData, error: eventsError },
        { data: tiersData, error: tiersError },
        { data: ticketsData, error: ticketsError },
        { data: bookingsData, error: bookingsError },
        { data: supportData, error: supportError },
      ] = await Promise.all([
        supabase.from("events").select("id, title, venue, date, time, status, created_at"),
        supabase.from("ticket_tiers").select("id, event_id, name, price, quantity_total"),
        fetchDashboardTickets(),
        supabase
          .from("bookings")
          .select("*")
          .order("created_at", { ascending: false })
          .limit(30),
        supabase.from("support_tickets").select("id, status, created_at"),
      ]);

      if (eventsError || tiersError || ticketsError) {
        const blockingError = eventsError || tiersError || ticketsError;
        console.error("Failed to load dashboard data:", blockingError);
        setDataError(`Dashboard data could not be loaded: ${blockingError?.message || "Unknown error"}`);
        setRawDashboardData({ events: [], tiers: [], tickets: [], bookings: [], supportTickets: [] });
        setEventRows([]);
        setScheduleEvents([]);
        setLoading(false);
        return;
      }

      const warnings = [];
      if (bookingsError) {
        console.warn("Bookings lookup blocked while loading dashboard:", bookingsError);
        warnings.push(
          `Booking requests could not be loaded: ${bookingsError.message || "Unknown error"}`
        );
      }
      if (supportError && !isMissingSupportTicketsSchemaError(supportError)) {
        console.warn("Support tickets lookup blocked while loading dashboard:", supportError);
        warnings.push("Support tickets could not be loaded.");
      } else if (supportError) {
        console.warn("Support tickets table is not set up yet:", supportError);
      }
      setDataError(warnings.join(" "));

      const events = eventsData || [];
      const tiers = tiersData || [];
      const tickets = ticketsData || [];
      const bookings = bookingsError ? [] : bookingsData || [];
      const supportTickets = supportError ? [] : supportData || [];

      setRawDashboardData({ events, tiers, tickets, bookings, supportTickets });

      const tierById = {};
      const tierTotalsByEvent = {};
      const soldByEvent = {};
      tiers.forEach((tier) => {
        tierById[tier.id] = tier;
        tierTotalsByEvent[tier.event_id] = (tierTotalsByEvent[tier.event_id] || 0) + Number(tier.quantity_total || 0);
      });

      tickets.forEach((ticket) => {
        const tier = tierById[ticket.ticket_tier_id];
        if (!tier || isRefundedTicket(ticket)) return;

        soldByEvent[tier.event_id] = (soldByEvent[tier.event_id] || 0) + 1;
      });

      const computedEvents = events.map((event) => {
        const computedStatus = getComputedStatus(event);
        const sold = soldByEvent[event.id] || 0;
        const total = tierTotalsByEvent[event.id] || 0;

        let statusLabel = "Draft";
        let statusClass = "bg-gray-100 text-gray-700";

        if (total > 0 && sold >= total) {
          statusLabel = "Sold Out";
          statusClass = "bg-red-100 text-red-700";
        } else if (computedStatus === "published") {
          statusLabel = "Selling";
          statusClass = "bg-orange-100 text-orange-700";
        } else if (computedStatus === "past") {
          statusLabel = "Past";
          statusClass = "bg-slate-200 text-slate-700";
        }

        return {
          ...event,
          computedStatus,
          sold,
          total,
          statusLabel,
          statusClass,
        };
      });

      const schedule = computedEvents.filter((event) => event.computedStatus !== "archived");
      const visibleEvents = [...schedule]
        .sort((a, b) => b.sold - a.sold)
        .slice(0, 6)
        .map((event) => ({
          id: event.id,
          title: event.title,
          date: event.date,
          time: event.time,
          sold: event.sold,
          total: event.total,
          statusLabel: event.statusLabel,
          statusClass: event.statusClass,
        }));

      const pendingBookingRows = bookings
        .filter((booking) => deriveAdminUiStatus(booking) === "in_review")
        .slice(0, 4)
        .map((booking) => ({
          id: booking.id,
          name: booking.requester_name || booking.event_name || "Booking request",
          date: booking.created_at,
        }));

      setScheduleEvents(schedule);
      setEventRows(visibleEvents);
      setPendingBookings(pendingBookingRows);
      setLoading(false);
    };

    fetchDashboardData();

    const dashboardChannel = supabase
      .channel("dashboard-live")
      .on("postgres_changes", { event: "*", schema: "public", table: "events" }, fetchDashboardData)
      .on("postgres_changes", { event: "*", schema: "public", table: "ticket_tiers" }, fetchDashboardData)
      .on("postgres_changes", { event: "*", schema: "public", table: "tickets" }, fetchDashboardData)
      .on("postgres_changes", { event: "*", schema: "public", table: "bookings" }, fetchDashboardData)
      .on("postgres_changes", { event: "*", schema: "public", table: "support_tickets" }, fetchDashboardData)
      .subscribe();

    return () => {
      dashboardChannel.unsubscribe();
    };
  }, []);

  const sharedRange = getSharedRange(panelRanges);

  const applyGlobalRange = (value) => {
    setPanelRanges(Object.fromEntries(STAT_PANEL_KEYS.map((key) => [key, value])));
    setOpenCardFilter("");
  };

  const eventFilterOptions = useMemo(() => {
    const selectable = (rawDashboardData.events || [])
      .filter((event) => ["published", "past"].includes(getComputedStatus(event)))
      .sort((a, b) => {
        const aTime = parseEventDateTime(a)?.getTime() || 0;
        const bTime = parseEventDateTime(b)?.getTime() || 0;
        return aTime - bTime;
      })
      .map((event) => ({
        value: String(event.id),
        label: `${event.title || "Untitled event"} — ${formatShortDate(event.date)}`,
      }));
    return [{ value: "", label: "All Events" }, ...selectable];
  }, [rawDashboardData.events]);

  useEffect(() => {
    if (!selectedEventId) return;
    if (!eventFilterOptions.some((option) => option.value === String(selectedEventId))) {
      setSelectedEventId("");
    }
  }, [eventFilterOptions, selectedEventId]);

  const selectedEvent = useMemo(
    () => (rawDashboardData.events || []).find((event) => String(event.id) === String(selectedEventId)) || null,
    [rawDashboardData.events, selectedEventId]
  );

  const stats = useMemo(
    () =>
      computeDashboardStats({
        events: rawDashboardData.events,
        tiers: rawDashboardData.tiers,
        tickets: rawDashboardData.tickets,
        supportTickets: rawDashboardData.supportTickets,
        ranges: panelRanges,
        eventId: selectedEvent?.id || "",
      }),
    [rawDashboardData, panelRanges, selectedEvent]
  );

  const scopedSoldTickets = useMemo(() => {
    const tierById = buildTierIndex(rawDashboardData.tiers);
    return filterTicketsForEvent(rawDashboardData.tickets || [], tierById, selectedEvent?.id || "").filter(
      (ticket) => !isRefundedTicket(ticket)
    );
  }, [rawDashboardData.tiers, rawDashboardData.tickets, selectedEvent]);

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (!event.target.closest('[data-filter-menu="true"]')) {
        setOpenCardFilter("");
      }

      if (!event.target.closest('[data-ticket-event-menu="true"]')) {
        setIsTicketEventMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, []);

  const filteredEventRows = useMemo(() => {
    if (!search.trim()) return eventRows;
    const needle = search.toLowerCase();
    return eventRows.filter((row) => row.title.toLowerCase().includes(needle));
  }, [eventRows, search]);

  const activeTicketEvents = useMemo(() => {
    return (rawDashboardData.events || [])
      .filter((event) => getComputedStatus(event) === "published")
      .sort((a, b) => {
        const aTime = parseEventDateTime(a)?.getTime() || 0;
        const bTime = parseEventDateTime(b)?.getTime() || 0;
        return aTime - bTime;
      });
  }, [rawDashboardData.events]);

  useEffect(() => {
    if (!activeTicketEvents.length) {
      setSelectedTicketEventId("");
      return;
    }

    const stillValid = activeTicketEvents.some((event) => event.id === selectedTicketEventId);
    if (!stillValid) {
      setSelectedTicketEventId(activeTicketEvents[0].id);
    }
  }, [activeTicketEvents, selectedTicketEventId]);

  const selectedTicketEvent = useMemo(() => {
    if (selectedEvent) return selectedEvent;
    return activeTicketEvents.find((event) => event.id === selectedTicketEventId) || null;
  }, [selectedEvent, activeTicketEvents, selectedTicketEventId]);

  const donutEventId = selectedTicketEvent?.id || "";

  const ticketTypeRows = useMemo(() => {
    if (!donutEventId) return [];

    const tierById = {};
    const relevantTierIds = new Set();

    (rawDashboardData.tiers || []).forEach((tier) => {
      tierById[tier.id] = tier;
      if (tier.event_id === donutEventId) {
        relevantTierIds.add(tier.id);
      }
    });

    if (!relevantTierIds.size) return [];

    const soldByTierName = {};
    (rawDashboardData.tickets || []).forEach((ticket) => {
      if (!relevantTierIds.has(ticket.ticket_tier_id) || isRefundedTicket(ticket)) return;
      const tier = tierById[ticket.ticket_tier_id];
      const tierName = tier?.name || "Ticket";
      soldByTierName[tierName] = (soldByTierName[tierName] || 0) + 1;
    });

    return Object.entries(soldByTierName)
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 5);
  }, [rawDashboardData.tiers, rawDashboardData.tickets, donutEventId]);

  const salesSeries = useMemo(() => {
    const tickets = scopedSoldTickets;
    if (salesRange === "day") {
      const bucketCount = 8;
      const stepMs = 3 * 60 * 60 * 1000;
      const nowMs = Date.now();
      const startMs = nowMs - bucketCount * stepMs;
      const buckets = Array.from({ length: bucketCount }, (_, i) => {
        const bucketStart = startMs + i * stepMs;
        const bucketEnd = bucketStart + stepMs;
        return {
          key: `h-${bucketStart}`,
          label: new Date(bucketEnd).toLocaleTimeString("en-PH", {
            timeZone: MANILA_TIME_ZONE,
            hour: "numeric",
          }),
          start: bucketStart,
          end: bucketEnd,
          value: 0,
        };
      });

      tickets.forEach((ticket) => {
        const ts = new Date(ticket.created_at).getTime();
        if (Number.isNaN(ts) || ts < startMs || ts > nowMs) return;

        const index = Math.min(bucketCount - 1, Math.max(0, Math.floor((ts - startMs) / stepMs)));
        buckets[index].value += 1;
      });

      return buckets.map(({ key, label, value }) => ({ key, label, value }));
    }

    if (salesRange === "week") {
      const todaySerial = getManilaDaySerial(new Date());
      if (todaySerial === null) return [];

      const startSerial = todaySerial - 6;
      const series = Array.from({ length: 7 }, (_, i) => {
        const serial = startSerial + i;
        const date = new Date(serial * DAY_MS);
        return {
          key: `d-${serial}`,
          label: date.toLocaleDateString("en-PH", { timeZone: MANILA_TIME_ZONE, weekday: "short" }),
          value: 0,
        };
      });

      tickets.forEach((ticket) => {
        const serial = getManilaDaySerial(ticket.created_at);
        if (serial === null || serial < startSerial || serial > todaySerial) return;
        series[serial - startSerial].value += 1;
      });

      return series;
    }

    if (salesRange === "month") {
      const bucketSize = 5;
      const bucketCount = 6;
      const todaySerial = getManilaDaySerial(new Date());
      if (todaySerial === null) return [];

      const startSerial = todaySerial - (bucketSize * bucketCount - 1);
      const series = Array.from({ length: bucketCount }, (_, i) => {
        const serial = startSerial + i * bucketSize;
        const date = new Date(serial * DAY_MS);
        return {
          key: `m-${serial}`,
          label: date.toLocaleDateString("en-PH", {
            timeZone: MANILA_TIME_ZONE,
            month: "short",
            day: "numeric",
          }),
          value: 0,
        };
      });

      tickets.forEach((ticket) => {
        const serial = getManilaDaySerial(ticket.created_at);
        if (serial === null || serial < startSerial || serial > todaySerial) return;
        const index = Math.min(bucketCount - 1, Math.floor((serial - startSerial) / bucketSize));
        series[index].value += 1;
      });

      return series;
    }

    const currentMonthIndex = getManilaMonthIndex(new Date());
    if (currentMonthIndex === null) return [];

    const startMonthIndex = currentMonthIndex - 11;
    const series = Array.from({ length: 12 }, (_, i) => {
      const monthIndex = startMonthIndex + i;
      const month = monthIndex % 12;
      return {
        key: `y-${monthIndex}`,
        label: MONTH_LABELS[month],
        value: 0,
      };
    });

    tickets.forEach((ticket) => {
      const monthIndex = getManilaMonthIndex(ticket.created_at);
      if (monthIndex === null || monthIndex < startMonthIndex || monthIndex > currentMonthIndex) return;
      series[monthIndex - startMonthIndex].value += 1;
    });

    return series;
  }, [scopedSoldTickets, salesRange]);

  const maxSalesValue = Math.max(1, ...salesSeries.map((item) => item.value));
  const salesPath = useMemo(() => {
    if (!salesSeries.length) return "";

    const width = 580;
    const height = 220;
    const stepX = width / Math.max(1, salesSeries.length - 1);

    return salesSeries
      .map((item, idx) => {
        const x = idx * stepX;
        const y = height - (item.value / maxSalesValue) * (height - 20);
        return `${x},${y}`;
      })
      .join(" ");
  }, [salesSeries, maxSalesValue]);

  const donutStyle = useMemo(() => {
    if (!ticketTypeRows.length) {
      return { background: "conic-gradient(#d1d5db 0% 100%)" };
    }

    const colors = ["#18b4a4", "#3b82f6", "#facc15", "#a855f7", "#f97316"];
    let cursor = 0;
    const total = ticketTypeRows.reduce((sum, row) => sum + row.count, 0) || 1;
    const parts = ticketTypeRows.map((row, idx) => {
      const pct = (row.count / total) * 100;
      const from = cursor;
      const to = cursor + pct;
      cursor = to;
      return `${colors[idx % colors.length]} ${from}% ${to}%`;
    });

    return { background: `conic-gradient(${parts.join(", ")})` };
  }, [ticketTypeRows]);

  return (
    <div className="flex min-h-screen bg-gray-100">
      <div className="flex-1 min-w-0">
        <AdminTopBar
          searchValue={search}
          onSearchChange={setSearch}
          searchPlaceholder="Search events..."
        />

        <main className="p-4 sm:p-6 md:p-10 space-y-6 bg-[#f3f3f3]">
          {dataError && (
            <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-xs text-amber-800">
              {dataError}
            </div>
          )}
          <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
            <div className="min-w-0">
              <h2 className="text-xl font-black text-gray-900">
                {selectedEvent ? selectedEvent.title : "All Events"}
              </h2>
              <p className="text-xs text-gray-500">
                {selectedEvent
                  ? `Statistics for this event only${selectedEvent.venue ? ` • ${selectedEvent.venue}` : ""}`
                  : "Statistics across all events"}
              </p>
            </div>
            <div className="flex flex-col gap-2 sm:flex-row sm:items-end">
              <label className="flex flex-col gap-1 text-[11px] font-bold uppercase text-gray-500">
                Range
                <select
                  value={sharedRange || "__mixed"}
                  onChange={(event) => applyGlobalRange(event.target.value)}
                  className="h-9 rounded-lg border border-gray-300 bg-white px-3 text-xs font-semibold normal-case text-gray-800 sm:w-40"
                >
                  {!sharedRange && (
                    <option value="__mixed" disabled>
                      Mixed
                    </option>
                  )}
                  {CARD_RANGE_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </label>
              <div className="flex flex-col gap-1 text-[11px] font-bold uppercase text-gray-500">
                Event
                <SearchableSelect
                  value={selectedEventId}
                  onChange={setSelectedEventId}
                  options={eventFilterOptions}
                  pinnedOption=""
                  placeholder="All events"
                  searchPlaceholder="Search events..."
                  emptyLabel="No matching events"
                  className="normal-case font-semibold sm:w-72"
                />
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
            {[
              {
                key: "revenue",
                title: "Total Revenue",
                value: formatCurrency(stats.revenue),
                badge: "live",
                badgeClass: "bg-green-100 text-green-700 uppercase",
                onOpen: selectedEvent ? () => navigate("/admin/tickets") : undefined,
              },
              {
                key: "ticketsSold",
                title: "Tickets Sold",
                value: String(stats.ticketsSold),
                subtitleSuffix:
                  selectedEvent && stats.capacityTotal > 0
                    ? `${stats.capacityPercent}% of capacity (${stats.soldAllTime} / ${stats.capacityTotal} all time)`
                    : "",
                onOpen: selectedEvent ? () => navigate("/admin/tickets") : undefined,
              },
              selectedEvent
                ? {
                    key: "activeEvents",
                    title: "Checked In",
                    value: String(stats.checkedIn),
                    subtitleSuffix: `of ${stats.soldAllTime} sold all time`,
                    badge: "scanned",
                    badgeClass: "bg-blue-100 text-blue-700 uppercase",
                    onOpen: () => navigate("/admin/tickets"),
                  }
                : {
                    key: "activeEvents",
                    title: "Active Events",
                    value: String(stats.activeEvents),
                    badge: "published",
                    badgeClass: "bg-emerald-100 text-emerald-700 uppercase",
                    onOpen: () => navigate("/admin/liveevents"),
                  },
              selectedEvent
                ? {
                    key: "supportTickets",
                    title: "Refunds",
                    value: String(stats.refunds),
                    subtitleSuffix: `${formatCurrency(stats.refundedAmount)} refunded`,
                    badge: "refunded",
                    badgeClass: "bg-red-100 text-red-700 uppercase",
                    onOpen: () => navigate("/admin/tickets"),
                  }
                : {
                    key: "supportTickets",
                    title: "Support Tickets",
                    value: String(stats.supportTickets),
                    subtitlePrefix: "Open or pending ",
                    badge: "open",
                    badgeClass: "bg-red-100 text-red-700 uppercase",
                    onOpen: () => navigate("/admin/support"),
                  },
            ].map((card) => (
              <StatCard
                key={card.key}
                title={card.title}
                value={loading ? "..." : card.value}
                subtitle={`${card.subtitlePrefix || ""}(${getRangeLabel(panelRanges[card.key])})${
                  card.subtitleSuffix ? ` • ${card.subtitleSuffix}` : ""
                }`}
                badge={card.badge}
                badgeClass={card.badgeClass}
                isFilterOpen={openCardFilter === card.key}
                onToggleFilter={() => setOpenCardFilter((prev) => (prev === card.key ? "" : card.key))}
                onSelectFilter={(value) => {
                  setPanelRanges((prev) => ({ ...prev, [card.key]: value }));
                  setOpenCardFilter("");
                }}
                selectedRange={panelRanges[card.key]}
                onOpen={card.onOpen}
              />
            ))}
          </div>

          <div className="grid grid-cols-1 xl:grid-cols-3 gap-4 items-stretch">
              <div className="xl:col-span-2 bg-white border border-gray-300 rounded-2xl p-4 sm:p-5 min-w-0 h-full flex flex-col">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between mb-3">
                  <h3 className="font-bold text-2xl text-gray-900 min-w-0 truncate">
                    Sales Overview
                    {selectedEvent && (
                      <span className="text-base font-semibold text-gray-500"> — {selectedEvent.title}</span>
                    )}
                  </h3>
                  <div className="flex flex-wrap gap-1 text-xs font-semibold shrink-0">
                    {SALES_RANGE_OPTIONS.map((option) => (
                      <button
                        key={option.value}
                        type="button"
                        onClick={() => setSalesRange(option.value)}
                        className={`px-2 py-1 rounded-md transition-colors ${
                          salesRange === option.value
                            ? "text-blue-600 bg-blue-50"
                            : "text-gray-400 hover:text-gray-600"
                        }`}
                        aria-pressed={salesRange === option.value}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                </div>
                {loading ? (
                  <div className="h-64 rounded-lg bg-gray-50 flex items-center justify-center text-gray-500 text-sm">Loading sales trend...</div>
                ) : (
                  <div className="h-64 rounded-lg bg-gray-50 p-4 relative">
                    <svg viewBox="0 0 580 230" className="w-full h-full">
                      {[0, 1, 2, 3, 4].map((i) => (
                        <line
                          key={i}
                          x1="0"
                          x2="580"
                          y1={20 + i * 50}
                          y2={20 + i * 50}
                          stroke="#e5e7eb"
                          strokeDasharray="4 4"
                        />
                      ))}
                      <polyline
                        fill="rgba(59, 130, 246, 0.14)"
                        stroke="none"
                        points={`0,230 ${salesPath} 580,230`}
                      />
                      <polyline
                        fill="none"
                        stroke="#73a7ff"
                        strokeWidth="2.2"
                        points={salesPath}
                      />
                    </svg>

                    <div className="absolute bottom-2 left-4 right-4 flex justify-between text-[11px] text-gray-600 font-semibold">
                      {salesSeries.map((item) => (
                        <span key={`label-${item.key}`}>{item.label}</span>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <div className="bg-white border border-gray-300 rounded-2xl p-4 min-w-0 h-full flex flex-col">
                <div className="flex flex-col gap-3 sm:flex-row sm:justify-between sm:items-start mb-2">
                  <div className="min-w-0">
                    <h3 className="font-bold text-2xl text-gray-900 truncate">{selectedTicketEvent?.title || "No Active Event"}</h3>
                    <p className="text-[11px] text-gray-500">
                      {selectedTicketEvent ? `Venue: ${selectedTicketEvent.venue || "-"}` : "Select an active event"}
                    </p>
                  </div>
                  {!selectedEvent && (
                    <div className="relative shrink-0" data-ticket-event-menu="true">
                      <button
                        onClick={() => setIsTicketEventMenuOpen((prev) => !prev)}
                        className="p-0.5 rounded hover:bg-gray-100"
                        data-ticket-event-menu="true"
                      >
                        <MoreHorizontal size={16} className="text-gray-500" />
                      </button>

                      {isTicketEventMenuOpen && (
                        <div
                          className="absolute top-7 right-0 w-52 rounded-lg border border-gray-200 bg-white shadow-xl z-20 max-h-64 overflow-y-auto"
                          data-ticket-event-menu="true"
                        >
                          {activeTicketEvents.length === 0 ? (
                            <p className="px-3 py-2 text-xs text-gray-500">No active events</p>
                          ) : (
                            activeTicketEvents.map((event) => (
                              <button
                                key={event.id}
                                onClick={() => {
                                  setSelectedTicketEventId(event.id);
                                  setIsTicketEventMenuOpen(false);
                                }}
                                className={`w-full px-3 py-2 text-left text-xs font-semibold hover:bg-gray-50 ${
                                  selectedTicketEventId === event.id ? "text-brand-maroon" : "text-gray-700"
                                }`}
                                data-ticket-event-menu="true"
                              >
                                {event.title}
                              </button>
                            ))
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {loading ? (
                  <div className="flex flex-1 min-h-56 items-center justify-center text-gray-500 text-sm">Loading ticket mix...</div>
                ) : !selectedTicketEvent ? (
                  <div className="flex flex-1 min-h-56 items-center justify-center text-gray-500 text-sm">No active event available.</div>
                ) : ticketTypeRows.length === 0 ? (
                  <div className="flex flex-1 min-h-56 items-center justify-center text-gray-500 text-sm">No ticket sales yet for this event.</div>
                ) : (
                  <div className="flex flex-1 flex-col items-center justify-center">
                    <div className="flex h-40 items-center justify-center">
                      <div className="w-40 h-40 rounded-full" style={donutStyle}>
                        <div className="w-20 h-20 rounded-full bg-white mx-auto mt-10" />
                      </div>
                    </div>

                    <div className="mt-2 grid w-full grid-cols-2 gap-2 text-[11px]">
                      {ticketTypeRows.slice(0, 4).map((row, idx) => {
                        const colors = ["bg-[#18b4a4]", "bg-[#3b82f6]", "bg-[#facc15]", "bg-[#a855f7]"];

                        return (
                          <div key={row.name} className="flex items-center gap-1.5 text-gray-700">
                            <span className={`w-2.5 h-2.5 rounded-sm ${colors[idx] || "bg-gray-400"}`} />
                            <span>{row.name}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
          </div>

          <AdminEventCalendar
            events={scheduleEvents}
            search={search}
            selectedEventId={selectedEventId}
            loading={loading}
            onOpenEvent={(eventId) => navigate(`/admin/create-event?eventId=${eventId}`)}
          />

          <div className="grid grid-cols-1 xl:grid-cols-3 gap-4 items-start">
              <div className="xl:col-span-2 bg-white border border-gray-300 rounded-2xl p-4">
                <h3 className="font-bold text-2xl text-gray-900 mb-3">Active Events</h3>
                <div className="overflow-x-auto custom-scrollbar rounded-lg border border-gray-200">
                  <table className="w-full min-w-[640px] border-collapse text-sm">
                    <thead>
                      <tr className={adminTableHead}>
                        <th className="px-4 py-3 text-left">Event Name</th>
                        <th className="px-4 py-3">Date</th>
                        <th className="px-4 py-3">Tickets Sold</th>
                        <th className="px-4 py-3 text-center">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {loading ? (
                        <tr>
                          <td colSpan="4" className={adminEmpty}>Loading events...</td>
                        </tr>
                      ) : filteredEventRows.length === 0 ? (
                        <tr>
                          <td colSpan="4" className={adminEmpty}>No matching events found.</td>
                        </tr>
                      ) : (
                        filteredEventRows.map((row, index) => (
                          <EventRow
                            key={row.id}
                            row={row}
                            index={index}
                            highlighted={Boolean(selectedEvent) && row.id === selectedEvent.id}
                          />
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="bg-white border border-gray-300 rounded-2xl p-4 min-w-0">
                <div className="flex flex-col gap-3 sm:flex-row sm:justify-between sm:items-start mb-2">
                  <h3 className="font-bold text-2xl text-gray-900 min-w-0">Pending Booking Requests</h3>
                  <Link
                    to="/admin/bookings"
                    className="text-xs font-bold uppercase text-brand-maroon hover:underline shrink-0"
                  >
                    View all
                  </Link>
                </div>

                {loading ? (
                  <p className="text-sm text-gray-500">Loading booking requests...</p>
                ) : pendingBookings.length === 0 ? (
                  <p className="text-sm text-gray-500">No pending booking requests.</p>
                ) : (
                  <ul className="space-y-0">
                    {pendingBookings.map((item) => (
                      <li key={item.id} className="border-b border-gray-200 last:border-b-0">
                        <button
                          type="button"
                          onClick={() => navigate(`/admin/bookings/${item.id}`)}
                          className="w-full py-3 flex items-center justify-between gap-2 min-w-0 text-left hover:bg-gray-50"
                        >
                          <div className="min-w-0">
                            <p className="text-sm text-gray-900 font-semibold truncate">{item.name}</p>
                            <p className="text-[10px] text-gray-500">({formatShortDate(toDayKey(item.date))})</p>
                          </div>
                          <ChevronRight size={14} className="text-gray-500 shrink-0" />
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default AdminDashboard;
